/**
 * Poller do acompanhamento (DEV-110): é o que faz a badge de resposta nova aparecer sem
 * a pessoa abrir o painel.
 *
 * Mora num módulo próprio, com o tempo e a visibilidade INJETADOS, porque é a peça com
 * mais regra e menos superfície visual — e `ui/widget.ts` não tem cobertura de teste.
 *
 * Quatro decisões embutidas:
 *
 * 1. **`setTimeout` que se reagenda, não `setInterval`.** Com `setInterval`, uma
 *    requisição lenta acumula disparos e a aba acorda com uma fila de pedidos. Aqui o
 *    próximo só é marcado quando o anterior termina — é o mesmo desenho do driver de
 *    sincronização do front.
 * 2. **Nada roda com a aba escondida.** O tick continua acontecendo, mas sem requisição:
 *    manter o loop vivo (em vez de parar e religar) evita depender de `visibilitychange`
 *    para ressuscitar, e a aba oculta não gasta rede nem orçamento do rate limit.
 * 3. **Sem sobreposição.** Um tick que chega com o anterior ainda em voo é descartado.
 * 4. **`parar()` é definitivo.** Marca o poller como morto ANTES de limpar o timer, então
 *    um `executar()` em voo que termine depois não reagenda nada. É o que sustenta a
 *    invariante do widget: o tempo de vida dele é o do componente que o montou, e um
 *    poller órfão continuaria batendo na API depois do `destruir()`.
 */
export interface Poller {
  /** Roda uma vez agora (se visível) e passa a repetir no intervalo. Idempotente. */
  iniciar(): void;
  /** Para de vez. Chamar de novo não faz nada; `iniciar()` depois disto religa. */
  parar(): void;
}

export function criarPoller(opts: {
  intervaloMs: number;
  visivel: () => boolean;
  executar: () => Promise<void>;
}): Poller {
  let timer: ReturnType<typeof setTimeout> | null = null;
  let vivo = false;
  let emVoo = false;

  const agendar = (): void => {
    if (!vivo) return;
    timer = setTimeout(tick, opts.intervaloMs);
  };

  const tick = (): void => {
    if (!vivo) return;
    // Aba escondida ou pedido anterior em voo: pula o TRABALHO, não o loop.
    if (emVoo || !opts.visivel()) {
      agendar();
      return;
    }
    emVoo = true;
    void opts
      .executar()
      .catch(() => {
        /* o executar já trata os erros dele; aqui é só não derrubar o loop */
      })
      .finally(() => {
        emVoo = false;
        agendar();
      });
  };

  return {
    iniciar(): void {
      if (vivo) return;
      vivo = true;
      tick();
    },
    parar(): void {
      vivo = false;
      if (timer !== null) {
        clearTimeout(timer);
        timer = null;
      }
    },
  };
}
